import { useEffect, useRef } from 'react';
import { trackEvent } from '@shared/services/analyticsTracker';
import { logError } from '@/lib/log';

/** How long the boot gate may hold before we call it a stall (Sentry-reported). */
const STALL_THRESHOLD_MS = 10_000;

/**
 * Pure telemetry on the post-auth boot gate (the "Breathing..." spinner in
 * AuthenticatedApp): measures how long the gate blocks before first render, so
 * we can see the real-world distribution before changing any behavior.
 *
 * Background (2026-07-30): a FullStory replay of the appledemo account showed
 * the gate held ~30s after a fast, successful login — the Firestore Listen
 * stream stalled in the iOS webview while control-channel traffic stayed
 * healthy. Nothing measured it: the stall throws no error, and both
 * session_start and page_view fire while the spinner is still up, so stalled
 * and healthy boots look identical in GA4, Sentry, and FullStory (unless the
 * user taps the spinner and leaves dead clicks).
 *
 * Emits:
 * - GA4 `boot_data_ready` { duration_ms, stalled } once per app boot when the
 *   gate releases — the full distribution, healthy boots included.
 * - Sentry `boot_gate_stall` (via logError) the moment the gate has been up
 *   for 10s — counts the pathological case even when the session ends before
 *   the gate ever releases.
 *
 * Observational only: renders nothing, changes nothing.
 */
export function useBootGateTelemetry(blocked: boolean): void {
    const startedAt = useRef<number | null>(null);
    const readyReported = useRef(false);
    const stallReported = useRef(false);

    useEffect(() => {
        if (readyReported.current) return;

        if (blocked) {
            if (startedAt.current === null) startedAt.current = performance.now();
            if (stallReported.current) return;
            // Remaining time to the threshold, not a fresh 10s — the effect can
            // re-run mid-block (blocked flickering false→true) and the clock
            // should count total time since the gate first appeared.
            const remaining = STALL_THRESHOLD_MS - (performance.now() - startedAt.current);
            const timer = setTimeout(() => {
                stallReported.current = true;
                logError(
                    new Error(
                        `Boot gate stalled: >${STALL_THRESHOLD_MS / 1000}s waiting for first Firestore snapshots after auth`,
                    ),
                    { context: 'boot_gate_stall' },
                );
            }, Math.max(remaining, 0));
            return () => clearTimeout(timer);
        }

        if (startedAt.current !== null) {
            readyReported.current = true;
            const durationMs = Math.round(performance.now() - startedAt.current);
            trackEvent('boot_data_ready', {
                duration_ms: durationMs,
                stalled: durationMs >= STALL_THRESHOLD_MS,
            });
        }
    }, [blocked]);
}
